import { useMemo, useState } from 'react';
import { IncrementalQuantityField } from '../../../components/forms/incremental-quantity-field.tsx';
import type { ButtonProps } from '../../../components/ui/button.tsx';
import { Button } from '../../../components/ui/button.tsx'; // Importing the hook to us
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { ProductVariantDTO } from '../../../server/dto/product-variant-dto.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { DraftMachineContext } from '../../sales-draft/machines/draft-machine.ts';
import type { SelectedLineItemProduct } from '../../sales-draft/types/normal-line-machine-types.ts';

type Props = {
    product: ProductVariantDTO;
} & Pick<ButtonProps, 'variant' | 'color'>;

const InventoryDetailsQuickAdd = ({ product: productVariant, variant = 'primary', color }: Props) => {
    const product = useMemo(() => {
        return {
            ...productVariant,
            article_group_id: productVariant.article_group_id ?? '',
            sales_units_per_package_unit: productVariant.sales_units_per_package_unit || 1,
            quantity_unit_sales: productVariant.quantity_unit_sales ?? '',
            package_unit: productVariant.package_unit ?? '',
        } satisfies SelectedLineItemProduct;
    }, [productVariant]);

    const [quantity, setQuantity] = useState(product.sales_units_per_package_unit); // Default quantity
    const { t } = useLocale('product-details'); // Hook to access the translation function
    const { toast } = useToast();

    const sendEvent = useAnalytics();

    const draftMachineRef = DraftMachineContext.useActorRef();

    const draftOrderId = DraftMachineContext.useSelector((s) => s.context.draftOrderId);

    return (
        <div className="flex items-center space-x-3">
            <IncrementalQuantityField
                className="w-[200px]"
                value={quantity}
                onValueChange={setQuantity}
                partialPackingType={product.partial_packing_type ?? undefined}
                quantityUnit={product.quantity_unit_sales}
                salesUnitsPerPackageUnit={product.sales_units_per_package_unit}
            />
            <Button
                variant={variant}
                color={color}
                onClick={() => {
                    draftMachineRef.send({
                        type: 'lineItems.add.normal',
                        payload: {
                            product,
                            quantity,
                        },
                    });

                    sendEvent('item_added_from_product_detail_page', {
                        draft_id: draftOrderId,
                        product_variant_id: product.product_variant_id,
                        article_group_id: product.article_group_id,
                        quantity,
                    });

                    toast({
                        icon: 'success',
                        variant: 'default',
                        title: t('toast__product_added_title', { ns: 'catalog' }),
                        description: t('toast__product_added_body', { ns: 'catalog' }).replace(
                            '{{quantity}}',
                            quantity.toString(),
                        ),
                    });
                }}
            >
                {t('quick_add')}
            </Button>
        </div>
    );
};

export default InventoryDetailsQuickAdd;
